import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Footer from '../../components/Footer/Footer';
import Menu from '../../components/Menu/Menu';
import Toast from '../../components/Toast/Toast';

import {
    getEmailPreview,
    getEmailPreviews,
    sendTestEmail,
} from '../../services/EmailService';

const MySwal = withReactContent(Swal);

function Emails() {
    const [templates, setTemplates] = useState([]);
    const [selectedType, setSelectedType] = useState('');
    const [preview, setPreview] = useState(null);
    const [device, setDevice] = useState('desktop');
    const [viewMode, setViewMode] = useState('html');
    const [notification, setNotification] = useState({ type: '', text: '' });

    useEffect(() => {
        getEmailPreviews()
            .then(result => {
                setTemplates(result);
                if (result && result.length) selectTemplate(result[0].type);
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function selectTemplate(type) {
        setSelectedType(type);
        getEmailPreview(type)
            .then(result => setPreview(result))
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }

    async function onSendTestClick() {
        const template = templates.find(item => item.type === selectedType);
        if (!template) return;

        const result = await MySwal.fire({
            title: 'Send test email',
            text: `The '${template.name}' template will be sent with sample data using the system email channel.`,
            input: 'email',
            inputPlaceholder: 'name@example.com',
            showCancelButton: true,
            confirmButtonColor: '#1F2937',
            cancelButtonColor: '#CA1A41',
            confirmButtonText: 'Send test',
            inputValidator: value =>
                !value ? 'Inform the destination email.' : undefined,
        });
        if (!result.isConfirmed) return;

        try {
            await sendTestEmail(selectedType, result.value.trim());
            setNotification({
                type: 'success',
                text: `Test email sent to ${result.value.trim()}!`,
            });
        } catch (err) {
            console.error(err.response ? err.response.data : err.message);
            setNotification({
                type: 'error',
                text: err.response ? err.response.data : err.message,
            });
        }
    }

    return (
        <>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <h1 className="h4">Email Templates</h1>
                        <p className="mb-0 text-muted small">
                            Preview of the emails Argos TraderBot sends, with
                            sample data.
                        </p>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4 mb-4">
                        <div className="card border-0 shadow">
                            <div className="list-group list-group-flush">
                                {templates.map(template => (
                                    <button
                                        key={template.type}
                                        type="button"
                                        className={`list-group-item list-group-item-action ${
                                            selectedType === template.type
                                                ? 'active'
                                                : ''
                                        }`}
                                        onClick={() =>
                                            selectTemplate(template.type)
                                        }
                                    >
                                        <div className="fw-bold">
                                            {template.name}
                                        </div>
                                        <div
                                            className={
                                                selectedType === template.type
                                                    ? 'small'
                                                    : 'small text-muted'
                                            }
                                        >
                                            {template.description}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="col-md-8 mb-4">
                        <div className="card card-body border-0 shadow">
                            <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
                                <div className="text-truncate me-3">
                                    <span className="fw-bold">Subject: </span>
                                    {preview ? preview.subject : '-'}
                                </div>
                                <div className="d-inline-flex align-items-center">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-primary me-2"
                                        onClick={onSendTestClick}
                                    >
                                        Send test
                                    </button>
                                    <div className="btn-group">
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${
                                                viewMode === 'html'
                                                    ? 'btn-primary'
                                                    : 'btn-outline-primary'
                                            }`}
                                            onClick={() => setViewMode('html')}
                                        >
                                            HTML
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${
                                                viewMode === 'text'
                                                    ? 'btn-primary'
                                                    : 'btn-outline-primary'
                                            }`}
                                            onClick={() => setViewMode('text')}
                                        >
                                            Text
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${
                                                device === 'desktop'
                                                    ? 'btn-primary'
                                                    : 'btn-outline-primary'
                                            }`}
                                            onClick={() => setDevice('desktop')}
                                        >
                                            Desktop
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${
                                                device === 'mobile'
                                                    ? 'btn-primary'
                                                    : 'btn-outline-primary'
                                            }`}
                                            onClick={() => setDevice('mobile')}
                                        >
                                            Mobile
                                        </button>
                                    </div>
                                </div>
                            </div>
                            {viewMode === 'html' ? (
                                <div
                                    className="d-flex justify-content-center"
                                    style={{
                                        backgroundColor: '#f4f5f7',
                                        borderRadius: '6px',
                                        padding: '16px',
                                    }}
                                >
                                    <iframe
                                        title="Email preview"
                                        srcDoc={preview ? preview.html : ''}
                                        sandbox=""
                                        style={{
                                            width:
                                                device === 'mobile'
                                                    ? '375px'
                                                    : '100%',
                                            height: '640px',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            backgroundColor: '#ffffff',
                                        }}
                                    />
                                </div>
                            ) : (
                                <pre
                                    className="mb-0 p-3"
                                    style={{
                                        backgroundColor: '#f4f5f7',
                                        borderRadius: '6px',
                                        minHeight: '640px',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {preview ? preview.text : ''}
                                </pre>
                            )}
                        </div>
                    </div>
                </div>
            </main>
            <Toast
                text={notification.text}
                type={notification.type}
                onShow={() => setNotification({ type: '', text: '' })}
            />
            <Footer />
        </>
    );
}

export default Emails;
